import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';
import { DEPLOY_DURATIONS } from '@cls/shared';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

interface Worker { id: string; name: string; role?: string }
interface Project { id: string; code: string; name: string; status: string; supervisorId?: string | null; clientPmId?: string | null; clsManagerId?: string | null; }
interface Contact { id: string; name: string; role: string | null; customer?: string | null; }

interface Props { onClose: () => void; }

export function ManualLogModal({ onClose }: Props) {
  const qc = useQueryClient();
  const [workerId, setWorkerId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState('07:30');
  const [duration, setDuration] = useState('1 day');
  const { values: durations } = useReferenceList('durations', DEPLOY_DURATIONS);
  const durationOptions = durations.includes(duration) ? durations : [duration, ...durations];
  const [status, setStatus] = useState('active');
  const [supervisorId, setSupervisorId] = useState('');
  const [supervisorAuto, setSupervisorAuto] = useState(false);
  const [meet, setMeet] = useState('');
  const [waSent, setWaSent] = useState(false);
  const [requesterId, setRequesterId] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<{ workerId?: boolean; projectId?: boolean; date?: boolean }>({});

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });

  useEffect(() => {
    if (supervisorAuto || !projectId) return;
    const p = projects.find((x) => x.id === projectId);
    if (!p) return;
    const def = p.supervisorId || p.clientPmId || p.clsManagerId || '';
    if (def && !supervisorId) setSupervisorId(def);
    setSupervisorAuto(true);
  }, [projectId, projects, supervisorId, supervisorAuto]);

  function validateAndSubmit() {
    const next: { workerId?: boolean; projectId?: boolean; date?: boolean } = {};
    if (!workerId) next.workerId = true;
    if (!projectId) next.projectId = true;
    if (!date) next.date = true;
    setErrors(next);
    if (Object.keys(next).length) {
      const missing: string[] = [];
      if (next.workerId) missing.push('Worker');
      if (next.projectId) missing.push('Project');
      if (next.date) missing.push('Start Date');
      toast('Missing required fields', missing.join(', '), 'warn');
      return;
    }
    save.mutate();
  }

  const save = useMutation({
    mutationFn: async () => api.post('/deployments', {
      workerId, projectId, startDate: date, startTime: time, duration, status,
      supervisorId: supervisorId || null, meet: meet.trim() || null,
      waMessageSent: !!waSent, requesterId: requesterId || null, notes: notes.trim() || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      toast.success('Manual record saved');
      onClose();
    },
    onError: () => toast.error('Save failed'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 600 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="ml-title">📋 Manual Deployment Entry</div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Worker</label>
            <SearchableSelect
              id="ml-worker"
              value={workerId}
              onChange={(v) => { setWorkerId(v); if (errors.workerId) setErrors((p) => ({ ...p, workerId: false })); }}
              placeholder="— Select worker —"
              invalid={errors.workerId}
              options={workers.map((w) => ({ value: w.id, label: `${w.name}${w.role ? ` — ${w.role}` : ''}` }))}
            />
          </div>
          <div className="form-row">
            <label className="form-label">Project</label>
            <SearchableSelect
              id="ml-project"
              value={projectId}
              onChange={(v) => { setProjectId(v); if (errors.projectId) setErrors((p) => ({ ...p, projectId: false })); setSupervisorAuto(false); }}
              placeholder="— Select project —"
              invalid={errors.projectId}
              options={projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
            />
          </div>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Start Date</label><input type="date" className="form-input" id="ml-date" value={date} onChange={(e) => { setDate(e.target.value); if (errors.date) setErrors((p) => ({ ...p, date: false })); }} style={errors.date ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 2px rgba(239,68,68,.15)' } : undefined} /></div>
          <div className="form-row"><label className="form-label">Start Time</label><input type="time" className="form-input" id="ml-time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Duration</label>
            <select className="form-select" id="ml-duration" value={duration} onChange={(e) => setDuration(e.target.value)}>
              {durationOptions.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Status</label>
            <select className="form-select" id="ml-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Supervisor</label>
            <SearchableSelect
              id="ml-sup"
              value={supervisorId}
              onChange={setSupervisorId}
              placeholder="— Select supervisor —"
              options={[{ value: '', label: '— Select supervisor —' }, ...contacts.map((c) => ({ value: c.id, label: `${c.name}${c.customer ? ` · ${c.customer}` : ''}${c.role ? ` — ${c.role}` : ''}` }))]}
            />
          </div>
          <div className="form-row"><label className="form-label">Who to Meet</label><input type="text" className="form-input" id="ml-meet" value={meet} onChange={(e) => setMeet(e.target.value)} /></div>
        </div>
        <div className="form-row">
          <label className="form-label">WhatsApp Sent?</label>
          <div style={{ display: 'flex', gap: 14, marginTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}><input type="radio" name="ml-wa" checked={waSent} onChange={() => setWaSent(true)} style={{ accentColor: '#25D366' }} /> Yes</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}><input type="radio" name="ml-wa" checked={!waSent} onChange={() => setWaSent(false)} style={{ accentColor: '#25D366' }} /> No</label>
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">Requested By</label>
          <SearchableSelect
            id="ml-requester"
            value={requesterId}
            onChange={setRequesterId}
            placeholder="— No requester —"
            options={[{ value: '', label: '— No requester —' }, ...contacts.map((c) => ({ value: c.id, label: `${c.name}${c.customer ? ` · ${c.customer}` : ''}${c.role ? ` — ${c.role}` : ''}` }))]}
          />
        </div>
        <div className="form-row"><label className="form-label">Notes</label><textarea className="form-textarea" id="ml-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" id="ml-save-btn" onClick={validateAndSubmit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save Record'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
